import Api from '@/services/Api';
import Vue from 'vue';
import RepositoryFactory from '@/services/repositories/RepositoryFactory';

const companyRepository = RepositoryFactory.get('company');
// Durante este intervalo se reutilizan los datos del comercial de la compañía.
// Cuando caduca, la siguiente selección permite comprobar si han cambiado.
const COMMERCIAL_CACHE_TTL = 10 * 60 * 1000;
// Las peticiones en curso también se comparten para evitar duplicados.
const commercialRequests = new Map();

function normalizeValue(value) {
  return String(value ?? '').trim();
}

function findContactCompany(rootState, companyCode) {
  // Los contactos reciben el comercial dentro de cada compañía en la
  // respuesta de autorización, por lo que no necesitan una petición extra.
  const normalizedCompanyCode = normalizeValue(companyCode);
  const contacts = rootState?.user?.authenticatedUser?.contacts || [];

  for (const contact of contacts) {
    const company = (contact?.companies || []).find(
      (item) => normalizeValue(item?.code) === normalizedCompanyCode
    );

    if (company) return company;
  }

  return null;
}

function findSelectedCurrency({
  addresses = [],
  addressCode = null,
  companyCode = null,
  enabled = true,
  clientMenuItems = [],
}) {
  const selectedAddress = Array.isArray(addresses)
    ? addresses.find(
        (address) =>
          normalizeValue(address?.addressCode) ===
          normalizeValue(addressCode)
      )
    : null;

  if (selectedAddress?.currency) {
    return selectedAddress.currency;
  }

  const matchedClientMenuItem = (
    Array.isArray(clientMenuItems) ? clientMenuItems : []
  ).find((item) => {
    const sameAddress =
      normalizeValue(item?.addressCode) ===
      normalizeValue(addressCode);
    const sameCompany = enabled
      ? normalizeValue(item?.addressCompany) ===
        normalizeValue(companyCode)
      : normalizeValue(item?.companyCode) ===
        normalizeValue(companyCode);

    return sameAddress && sameCompany;
  });

  return matchedClientMenuItem?.currency || null;
}

export default {
  namespaced: true,
  state: {
    enabled: true,
    address: '',
    addressName: '',
    company: '',
    companyName: '',
    currency: null,
    showNewContactPopup: false,
    editData: {
      isEditing: false,
      data: null,
    },
    addresses: [],
    contactIndex: 0,
    hasAccessToAdvanceService: false,
    hasAccessToNutritionalMonitoring: false,
    // Cachés indexadas por el código de compañía para el acceso de empleados.
    commercialsByCompany: {},
    commercialLoadingByCompany: {},
    commercialUpdatedAtByCompany: {},
  },
  getters: {
    contactInfo(state, getters, rootState) {
      const filteredAddresses =
        state.addresses?.filter(
          (x) => x.addressCode === state.address
        ) || [];

      // Obtén el primer match o un objeto vacío
      const selectedAddress = filteredAddresses[0] || {};
      const companyCode = normalizeValue(state.company);
      const companyFromLogin = findContactCompany(
        rootState,
        companyCode
      );
      // Para contactos se usa la información del login. Para empleados se
      // usa la respuesta guardada de /company/agent para la compañía activa.
      const commercial = state.enabled
        ? companyFromLogin || selectedAddress
        : state.commercialsByCompany[companyCode] || {};

      return {
        address: state.address,
        addressName: state.addressName,
        company: state.company,
        companyName: state.companyName,
        country: selectedAddress.country || null,
        city: selectedAddress.city || null,
        province: selectedAddress.province || null,
        desc_comuna: selectedAddress.desc_comuna || null,
        fax: selectedAddress.fax || null,
        phone: selectedAddress.phone || null,
        identification_number:
          selectedAddress.identification_number || null,
        currency: selectedAddress.currency || state.currency || null,
        language: selectedAddress.language || null,
        addresses: state.addresses,
        adressesCity:
          state.addresses?.map((address) => ({
            ...address,
            name: [address.name, address.city, address.province]
              .map((v) => (v ?? '').toString().trim())
              .filter(Boolean) // quita vacíos
              .join(' - '), // "name - city - province"
          })) || [],
        agent: commercial.agent ?? null,
        code_agent: commercial.code_agent ?? null,
        agent_email: commercial.agent_email ?? null,
        commercialLoading:
          !state.enabled &&
          Boolean(state.commercialLoadingByCompany[companyCode]),
      };
    },
  },

  actions: {
    setEnabled({ commit }, value) {
      commit('setEnabled', value);
    },
    setAddressName({ commit }, value) {
      commit('setAddressName', value);
    },
    setAddress({ commit, state, rootState }, value) {
      commit('setAddress', value);

      commit(
        'setCurrency',
        findSelectedCurrency({
          addresses: state.addresses,
          addressCode: value,
          companyCode: state.company,
          enabled: state.enabled,
          clientMenuItems: rootState.clientMenu?.items,
        }) || state.currency
      );
    },
    setCurrency({ commit }, value) {
      commit('setCurrency', value);
    },
    setCompany({ commit, state, dispatch, rootState }, value) {
      const previousCompany = state.company;

      commit('setCompany', value);
      commit(
        'setCurrency',
        findSelectedCurrency({
          addresses: state.addresses,
          addressCode: state.address,
          companyCode: value,
          enabled: state.enabled,
          clientMenuItems: rootState.clientMenu?.items,
        }) || state.currency
      );

      if (!state.enabled && normalizeValue(value)) {
        // La compañía activa se actualiza antes de iniciar esta petición.
        // El action mantiene la caché y absorbe los errores para no bloquear
        // el cambio de compañía.
        dispatch('loadCompanyAgent', value);
      }

      const shouldLogLogin =
        !rootState.user?.authenticatedUser?.is_agq &&
        !!rootState.user?.authenticatedUser?.email;

      // Sólo para contactos: loguea cuando ya había empresa previa y cambia.
      if (
        shouldLogLogin &&
        value &&
        !!previousCompany &&
        previousCompany !== value
      ) {
        dispatch('user/logLogin', null, { root: true });
      }
    },
    setCompanyName({ commit }, value) {
      commit('setCompanyName', value);
    },
    setShowNewContactPopup({ commit }, value) {
      commit('setShowNewContactPopup', value);
    },
    setEditData({ commit }, value) {
      commit('setEditData', value);
    },
    setAddresses({ commit, state }, value) {
      commit('setAddresses', value);

      const selectedAddress = Array.isArray(value)
        ? value.find(
            (address) => address?.addressCode === state.address
          )
        : null;

      commit('setCurrency', selectedAddress?.currency || null);
    },
    setContactIndex({ commit }, value) {
      commit('setContactIndex', value);
    },
    async loadCompanyAgent(
      { commit, state, rootState },
      companyCode
    ) {
      // Este action solo se utiliza para empleados. El cambio de compañía ya
      // se ha confirmado antes de llegar aquí y nunca se propaga el error a
      // la navegación si el servicio no está disponible.
      const normalizedCompanyCode = normalizeValue(companyCode);
      if (!normalizedCompanyCode || state.enabled) return null;

      const now = Date.now();
      const updatedAt =
        state.commercialUpdatedAtByCompany[normalizedCompanyCode] ||
        0;
      const cachedCommercial =
        state.commercialsByCompany[normalizedCompanyCode];

      if (
        cachedCommercial &&
        now - updatedAt < COMMERCIAL_CACHE_TTL
      ) {
        return cachedCommercial;
      }

      if (commercialRequests.has(normalizedCompanyCode)) {
        return commercialRequests.get(normalizedCompanyCode);
      }

      commit('setCommercialLoading', {
        companyCode: normalizedCompanyCode,
        value: true,
      });

      const request = companyRepository
        .getAgent(rootState.user.accessToken, normalizedCompanyCode)
        .then((response) => {
          // Se aceptan tanto la respuesta directa del endpoint como una
          // respuesta envuelta en data, manteniendo siempre los tres campos.
          const responseData = response?.data ?? response ?? {};
          const commercial = responseData?.data ?? responseData;
          const normalizedCommercial = {
            agent: commercial?.agent ?? null,
            code_agent: commercial?.code_agent ?? null,
            agent_email: commercial?.agent_email ?? null,
          };

          commit('setCommercial', {
            companyCode: normalizedCompanyCode,
            commercial: normalizedCommercial,
          });
          commit('setCommercialUpdatedAt', {
            companyCode: normalizedCompanyCode,
            value: Date.now(),
          });

          return normalizedCommercial;
        })
        .catch(() => {
          // El fallo no impide cambiar de compañía; se deja la caché sin
          // actualizar para que una selección posterior pueda reintentarlo.
          return null;
        })
        .finally(() => {
          commit('setCommercialLoading', {
            companyCode: normalizedCompanyCode,
            value: false,
          });
          commercialRequests.delete(normalizedCompanyCode);
        });

      commercialRequests.set(normalizedCompanyCode, request);
      return request;
    },
    async edit({ rootState }, data) {
      try {
        await Api.updateContact(
          {
            name: data.name,
            surname: data.surname,
            companyId: data.companyId,
            addresses: data.addresses,
            phone: data.phone,
            language: data.language,
          },
          data.contactCode,
          rootState.contact.company,
          rootState.contact.address
        );
        return Promise.resolve();
      } catch (error) {
        return Promise.reject(error);
      }
    },
    async create({ rootState }, data) {
      try {
        const res = await Api.createContact(
          {
            name: data.name,
            surname: data.surname,
            email: data.email,
            companyId: data.companyId,
            addresses: data.addresses,
            phone: data.phone,
            language: data.language,
          },
          rootState.contact.company,
          rootState.contact.address
        );

        if (res.status === 400) {
          const alreadyExists = await res.json();

          return Promise.reject(
            new Error(
              alreadyExists.error.includes('already exists')
                ? 'mailAlreadyExists'
                : undefined
            )
          );
        }
        return Promise.resolve();
      } catch (error) {
        return Promise.reject(error);
      }
    },
    async checkAccessToAdvanceService({ commit, rootState }) {
      try {
        const xhr = await Api.checkAccessToAdvancedServices(
          rootState.contact.company,
          rootState.contact.address
        );
        const response = JSON.parse(xhr.response);

        commit(
          'setHasAccessToAdvanceService',
          response?.access_to_advance_services ?? false
        );

        return Promise.resolve();
      } catch (error) {
        return Promise.reject(error);
      }
    },
    async checkAccessToNutritionalMonitoring({ commit, rootState }) {
      try {
        const xhr = await Api.getNutritionalMonitoringWithoutGeometry(
          rootState.contact.company,
          rootState.contact.address
        );
        const response = JSON.parse(xhr.response);

        commit(
          'setHasAccessToNutritionalMonitoring',
          response?.data?.length > 0 ?? false
        );

        return Promise.resolve();
      } catch (error) {
        return Promise.reject(error);
      }
    },
    resetData({ commit }) {
      commit('resetData');
    },
  },
  mutations: {
    setEnabled(state, value) {
      state.enabled = value;
    },
    setAddressName(state, value) {
      state.addressName = value;
    },
    setAddress(state, value) {
      state.address = value;
    },
    setCompany(state, value) {
      state.company = value;
    },
    setCompanyName(state, value) {
      state.companyName = value;
    },
    setCurrency(state, value) {
      state.currency = value;
    },
    setShowNewContactPopup(state, value) {
      state.showNewContactPopup = value;
    },
    setEditData(state, editData) {
      state.editData = editData;
    },
    setAddresses(state, value) {
      state.addresses = value;
    },
    setContactIndex(state, value) {
      state.contactIndex = value;
    },
    setHasAccessToAdvanceService(state, value) {
      state.hasAccessToAdvanceService = value;
    },
    setHasAccessToNutritionalMonitoring(state, value) {
      state.hasAccessToNutritionalMonitoring = value;
    },
    setCommercial(state, { companyCode, commercial }) {
      // Vue.set mantiene reactiva una clave de compañía creada dinámicamente.
      Vue.set(state.commercialsByCompany, companyCode, commercial);
    },
    setCommercialLoading(state, { companyCode, value }) {
      // Solo se persisten las cargas activas; las entradas finalizadas se
      // eliminan para no acumular valores false por cada compañía visitada.
      if (value) {
        Vue.set(state.commercialLoadingByCompany, companyCode, true);
      } else {
        Vue.delete(state.commercialLoadingByCompany, companyCode);
      }
    },
    setCommercialUpdatedAt(state, { companyCode, value }) {
      Vue.set(state.commercialUpdatedAtByCompany, companyCode, value);
    },
    resetData(state) {
      state.enabled = true;
      state.address = '';
      state.addressName = '';
      state.company = '';
      state.companyName = '';
      state.currency = null;
      state.showNewContactPopup = false;
      state.addresses = [];
      state.contactIndex = 0;
      state.hasAccessToAdvanceService = false;
      state.hasAccessToNutritionalMonitoring = false;
      state.commercialsByCompany = {};
      state.commercialLoadingByCompany = {};
      state.commercialUpdatedAtByCompany = {};
    },
  },
};
